import { useState } from 'react'
import { UserPlus, Users } from 'lucide-react'
import type { CreatorMember, CreatorMemberFilter, CreatorPool, MemberSort, PoolSource } from '@/api/client'
import { SOURCE_LABEL, fmtCount, fmtRate, usePoolMembers } from '@/lib/creators'
import { formatShort } from '@/lib/time'
import {
  Button,
  DataTable,
  Dialog,
  EmptyState,
  FilterBar,
  FilterSelect,
  PlatformMark,
  SearchInput,
  Segmented,
  StatusPill,
  useQueryParam,
  type Column,
} from '@/ui'
import { Section } from '../campaigns/shared'
import { AddPeople } from './PoolCards'

const PAGE = 100

const BANDS: Record<string, { label: string; min?: number; max?: number }> = {
  '': { label: '팔로워 전체' },
  lt1k: { label: '1천 미만', max: 999 },
  '1k': { label: '1천–1만', min: 1000, max: 9999 },
  '10k': { label: '1만–10만', min: 10000, max: 99999 },
  '100k': { label: '10만 이상', min: 100000 },
}

const SORTS: { value: MemberSort; label: string }[] = [
  { value: 'score', label: '점수순' },
  { value: 'followers', label: '팔로워순' },
  { value: 'engagement', label: '참여율순' },
  { value: 'added', label: '최근 추가순' },
]

const SOURCES = [{ value: '', label: '경로 전체' }, ...Object.entries(SOURCE_LABEL).map(([value, label]) => ({ value, label }))]

const SCORES = [
  { value: '', label: '점수 전체' },
  { value: '40', label: '40점 이상' },
  { value: '60', label: '60점 이상' },
  { value: '80', label: '80점 이상' },
]

function Handle({ m }: { m: CreatorMember }) {
  const c = m.creator
  return (
    <span className="flex min-w-0 flex-col">
      <span className="flex min-w-0 items-center gap-1.5">
        <PlatformMark platform={c.platform} />
        <span className="truncate font-medium">@{c.handle}</span>
      </span>
      {c.display_name && <span className="truncate text-meta text-muted-foreground">{c.display_name}</span>}
    </span>
  )
}

function LatestPost({ m }: { m: CreatorMember }) {
  const post = (m.creator.posts ?? [])[0]
  if (!post) return <span className="text-muted-foreground">—</span>
  const text = post.text.length > 70 ? `${post.text.slice(0, 70)}…` : post.text || '(글 없음)'
  return (
    <span className="block min-w-0">
      <span className="line-clamp-2 break-words">{text}</span>
      {post.posted_at && <span className="text-micro text-muted-foreground">{formatShort(post.posted_at)}</span>}
    </span>
  )
}

function Score({ m }: { m: CreatorMember }) {
  return (
    <span className="block min-w-0" title={m.score_reason || undefined}>
      <span className="font-semibold tabular-nums">{Math.round(m.score)}</span>
      {m.score_reason && <span className="line-clamp-2 break-words text-meta text-muted-foreground">{m.score_reason}</span>}
    </span>
  )
}

const columns: Column<CreatorMember>[] = [
  { key: 'who', header: '아이디 · 이름', cell: (m) => <Handle m={m} />, card: 'title', className: 'max-w-[14rem]' },
  {
    key: 'status',
    header: '상태',
    cell: (m) =>
      m.suppressed ? <StatusPill domain="creator" status="do_not_contact" /> : <StatusPill domain="poolMember" status={m.status} />,
    card: 'meta',
  },
  { key: 'followers', header: '팔로워', cell: (m) => <span className="tabular-nums">{fmtCount(m.creator.followers)}</span>, align: 'right' },
  {
    key: 'rate',
    header: '참여율',
    cell: (m) => <span className="tabular-nums">{m.creator.engagement_rate > 0 ? fmtRate(m.creator.engagement_rate) : '—'}</span>,
    align: 'right',
  },
  { key: 'post', header: '최근 글', cell: (m) => <LatestPost m={m} />, className: 'max-w-[18rem]', cardHide: (m) => !(m.creator.posts ?? []).length },
  { key: 'score', header: '점수', cell: (m) => <Score m={m} />, className: 'max-w-[16rem]' },
  {
    key: 'source',
    header: '찾은 경로',
    cell: (m) => (
      <span className="block min-w-0 break-words">
        {SOURCE_LABEL[m.source] ?? m.source}
        {m.found_by && <span className="block truncate text-meta text-muted-foreground">{m.found_by}</span>}
      </span>
    ),
  },
]

/** 사람 — the pool's people with filters and sort; a row opens the person. */
export function Members({ pool, onOpen }: { pool: CreatorPool; onOpen: (creatorId: number) => void }) {
  const [q, setQ] = useQueryParam('q')
  const [band, setBand] = useQueryParam('band')
  const [score, setScore] = useQueryParam('score')
  const [source, setSource] = useQueryParam('source')
  const [state, setState] = useQueryParam('show', 'in')
  const [sort, setSort] = useQueryParam('sort', 'score')
  const [limit, setLimit] = useState(PAGE)
  const [adding, setAdding] = useState(false)

  const b = BANDS[band] ?? BANDS['']
  const filter: CreatorMemberFilter = {
    q: q.trim() || undefined,
    status: state === 'removed' ? 'removed' : 'in',
    source: (source || undefined) as PoolSource | undefined,
    minFollowers: b.min,
    maxFollowers: b.max,
    minScore: score ? Number(score) : undefined,
    sort: sort as MemberSort,
    limit,
  }
  const res = usePoolMembers(pool.id, filter)
  const items = res.data?.items
  const total = res.data?.total ?? 0
  const filtered = !!(q || band || score || source)

  const addButton = (
    <Button size="sm" icon={UserPlus} onClick={() => setAdding(true)}>
      사람 추가
    </Button>
  )

  return (
    <Section title="사람" aside={res.data ? `${fmtCount(total)}명` : undefined} actions={addButton}>
      <FilterBar>
        <SearchInput value={q} onChange={setQ} placeholder="아이디·이름 찾기" />
        <FilterSelect
          label="팔로워 구간"
          value={band}
          onChange={setBand}
          options={Object.entries(BANDS).map(([value, v]) => ({ value, label: v.label }))}
        />
        <FilterSelect label="최소 점수" value={score} onChange={setScore} options={SCORES} />
        <FilterSelect label="찾은 경로" value={source} onChange={setSource} options={SOURCES} />
        <FilterSelect label="정렬" value={sort} onChange={setSort} options={SORTS} />
        <Segmented
          label="보기"
          size="sm"
          value={state === 'removed' ? 'removed' : 'in'}
          onChange={setState}
          options={[
            { value: 'in', label: '풀에 있음' },
            { value: 'removed', label: '뺀 사람' },
          ]}
          className="ml-auto"
        />
      </FilterBar>
      <DataTable
        label="풀의 사람"
        columns={columns}
        rows={items}
        rowKey={(m) => m.creator.id}
        onRowClick={(m) => onOpen(m.creator.id)}
        loading={res.isPending}
        error={res.error && !res.data ? res.error : undefined}
        onRetry={() => res.refetch()}
        rowClassName={(m) => (m.status === 'removed' ? 'opacity-70' : undefined)}
        empty={
          <EmptyState
            icon={Users}
            title={filtered ? '조건에 맞는 사람이 없다' : state === 'removed' ? '뺀 사람이 없다' : '아직 사람이 없다'}
            description={filtered ? '필터를 풀어 본다.' : state === 'removed' ? undefined : '지금 찾기를 누르거나 아이디를 붙여 넣어 채운다.'}
          />
        }
      />
      {items && total > items.length && (
        <div className="flex items-center justify-between gap-2 text-meta text-muted-foreground">
          <span className="tabular-nums">
            {fmtCount(items.length)} / {fmtCount(total)}명
          </span>
          {limit < 500 ? (
            <Button size="sm" onClick={() => setLimit((n) => Math.min(500, n + PAGE))}>
              더 보기
            </Button>
          ) : (
            <span>한 번에 500명까지 보인다. 필터로 좁힌다.</span>
          )}
        </div>
      )}
      <Dialog open={adding} onClose={() => setAdding(false)} title="사람 추가" size="lg">
        <div className="pb-3">
          <AddPeople pool={pool} />
        </div>
      </Dialog>
    </Section>
  )
}
